import { Component, Input, ChangeDetectorRef, OnChanges, SimpleChanges, inject } from '@angular/core';
import { LogError, Metadata, RunView } from '@memberjunction/core';
import { marketingAuthorizedUserEntity } from 'mj_generatedentities';
import {
    AuthorizedUserRow,
    ROLE_OPTIONS,
    RoleTierLocal,
    UserOption,
    grantableUsers,
    isRole,
    toAuthorizedUserRow,
} from './admin-users-and-roles.helpers';

/**
 * Admin · Users & Roles (issue #938).
 *
 * Lists `Authorized Users` rows for the active company and lets an Admin
 * grant new access, change a user's role, or revoke access entirely.
 *
 * Route gating (`requiredRole: 'Admin'` in `marketing-workspace.routes.config.ts`)
 * keeps non-Admins from reaching this surface in the sub-nav. Server-side
 * enforcement is implicit: every write here goes through the framework's
 * entity save/delete path, which (in the future) will be filtered by RLS
 * tied to AuthorizedUser. For day-1, the surface is gated on the UI side
 * only — same posture as the rest of the v1 Marketing access gate.
 *
 * Pure helpers (toAuthorizedUserRow / grantableUsers / isRole) live in
 * `./admin-users-and-roles.helpers.ts` so they can be unit-tested without
 * an Angular harness.
 */

@Component({
    standalone: false,
    selector: 'mkt-ws-admin-users-and-roles',
    template: `
<mkt-ws-page-head
    [eyebrow]="'Admin · Users & Roles' + (companyName ? ' · ' + companyName : '')"
    title="Users & Roles"
    sub="Grant marketing access, change roles, or revoke. Admin-only.">
</mkt-ws-page-head>

<div *ngIf="loading" class="aur__loading"><i class="fa-solid fa-spinner fa-spin"></i> Loading users…</div>

<ng-container *ngIf="!loading">
    <!-- Grant access form -->
    <section class="aur__grant">
        <div class="aur__caps">Grant access</div>
        <div class="aur__grant-row">
            <select class="aur__select aur__select--user" [(ngModel)]="grantUserId" name="grantUser">
                <option value="" disabled>Select a user…</option>
                <option *ngFor="let u of grantableUsers" [value]="u.ID">{{ u.Name }} <ng-container *ngIf="u.Email">&lt;{{ u.Email }}&gt;</ng-container></option>
            </select>
            <select class="aur__select" [(ngModel)]="grantRole" name="grantRole">
                <option *ngFor="let r of roleOptions" [value]="r">{{ r }}</option>
            </select>
            <button type="button" class="aur__btn aur__btn--primary"
                    [disabled]="!grantUserId || saving"
                    (click)="onGrant()">
                <i class="fa-solid fa-plus"></i> Grant
            </button>
        </div>
        <div *ngIf="grantableUsers.length === 0" class="aur__hint">All users with platform access are already on the allow-list for this company.</div>
    </section>

    <!-- Active rows -->
    <section class="aur__list">
        <div class="aur__caps">Authorized users · {{ rows.length }}</div>
        <div *ngIf="rows.length === 0" class="aur__empty">No users authorized for this company yet — use the form above to grant the first one.</div>

        <div *ngFor="let row of rows" class="aur__row">
            <div class="aur__row-main">
                <div class="aur__row-name">{{ row.userName || '(unnamed)' }}</div>
                <div class="aur__row-email">{{ row.userEmail || '—' }}</div>
            </div>
            <div class="aur__row-controls">
                <select class="aur__select aur__select--role"
                        [ngModel]="row.role"
                        (ngModelChange)="onRoleChange(row, $event)"
                        [disabled]="saving">
                    <option *ngFor="let r of roleOptions" [value]="r">{{ r }}</option>
                </select>
                <button type="button" class="aur__btn aur__btn--danger"
                        [disabled]="saving"
                        (click)="onRevoke(row)">
                    <i class="fa-solid fa-trash"></i> Revoke
                </button>
            </div>
            <div *ngIf="rowMessage[row.id]" class="aur__row-msg" [class.aur__row-msg--err]="rowMessageIsError[row.id]">
                {{ rowMessage[row.id] }}
            </div>
        </div>
    </section>
</ng-container>
    `,
    styles: [`
        :host { display: block; }
        .aur__loading { padding: 60px 20px; text-align: center; color: var(--mj-text-secondary, #71717a); }
        .aur__caps { font-size: 10.5px; letter-spacing: 0.11em; text-transform: uppercase; font-weight: 500; color: var(--mj-text-secondary, #71717a); margin-bottom: 10px; }
        .aur__grant { margin-bottom: 24px; padding: 16px; background: var(--mj-bg-surface, #fff); border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 8px; }
        .aur__grant-row { display: flex; gap: 8px; align-items: center; }
        .aur__hint { font-size: 12px; color: var(--mj-text-secondary, #71717a); margin-top: 8px; }
        .aur__select { padding: 6px 10px; border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 6px; background: var(--mj-bg-surface, #fff); font-size: 13px; font-family: inherit; }
        .aur__select--user { flex: 1; min-width: 240px; }
        .aur__select--role { width: 110px; }
        .aur__btn { display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px; border-radius: 6px; font-size: 12.5px; font-weight: 500; border: 1px solid var(--mj-border-default, #e0e0e0); background: var(--mj-bg-surface, #fff); color: var(--mj-text-primary, #1a1a1a); cursor: pointer; font-family: inherit; }
        .aur__btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .aur__btn--primary { background: var(--mj-brand-primary, #6366f1); color: var(--mj-text-inverse, #fff); border-color: var(--mj-brand-primary, #6366f1); }
        .aur__btn--danger { color: var(--mj-color-danger-600, #b91c1c); }
        .aur__btn--danger:hover:not(:disabled) { background: var(--mj-color-danger-50, #fef2f2); }
        .aur__list { padding: 0; }
        .aur__empty { padding: 24px; text-align: center; font-size: 13px; color: var(--mj-text-secondary, #71717a); border: 1px dashed var(--mj-border-default, #e0e0e0); border-radius: 8px; }
        .aur__row { display: grid; grid-template-columns: 1fr auto; gap: 16px; align-items: center; padding: 12px 14px; background: var(--mj-bg-surface, #fff); border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 8px; margin-bottom: 6px; }
        .aur__row-main { min-width: 0; }
        .aur__row-name { font-size: 13.5px; font-weight: 500; color: var(--mj-text-primary, #1a1a1a); }
        .aur__row-email { font-size: 12px; color: var(--mj-text-secondary, #71717a); margin-top: 2px; }
        .aur__row-controls { display: flex; align-items: center; gap: 8px; }
        .aur__row-msg { grid-column: 1 / -1; font-size: 12px; color: var(--mj-color-success-700, #15803d); margin-top: 4px; }
        .aur__row-msg--err { color: var(--mj-color-danger-700, #b91c1c); }
    `]
})
export class AdminUsersAndRolesSection implements OnChanges {
    @Input() companyId = '';
    @Input() companyName = '';

    private cdr = inject(ChangeDetectorRef);

    readonly roleOptions: ReadonlyArray<RoleTierLocal> = ROLE_OPTIONS;

    loading = false;
    saving = false;
    rows: AuthorizedUserRow[] = [];
    allUsers: UserOption[] = [];

    grantUserId = '';
    grantRole: RoleTierLocal = 'Operator';

    rowMessage: Record<string, string> = {};
    rowMessageIsError: Record<string, boolean> = {};

    /** Users not currently in the AuthorizedUser allow-list for this company. */
    get grantableUsers(): UserOption[] {
        return grantableUsers(this.allUsers, this.rows);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['companyId'] && this.companyId) {
            void this.load();
        }
    }

    private async load(): Promise<void> {
        if (!this.companyId) return;
        this.loading = true;
        this.cdr.detectChanges();
        try {
            const rv = new RunView();
            const [auth, users] = await rv.RunViews([
                {
                    EntityName: 'Authorized Users',
                    ExtraFilter: `CompanyID='${this.companyId}'`,
                    OrderBy: 'User ASC',
                    MaxRows: 500,
                    ResultType: 'entity_object',
                },
                {
                    // Pull a generous list of users so the grant dropdown can pick from any
                    // active platform user. RunView is server-paginated; 500 is plenty for
                    // the admin grant UX. Entity is registered as 'MJ: Users' (not 'Users').
                    EntityName: 'MJ: Users',
                    ResultType: 'simple',
                    Fields: ['ID', 'Name', 'Email'],
                    OrderBy: 'Name',
                    MaxRows: 500,
                },
            ]);
            if (auth.Success) {
                const entities = (auth.Results || []) as marketingAuthorizedUserEntity[];
                this.rows = entities.map(e => this.toRow(e));
            } else {
                LogError(`[AdminUsersAndRoles] Authorized Users query failed: ${auth.ErrorMessage}`);
            }
            if (users.Success) {
                this.allUsers = (users.Results || []) as UserOption[];
            } else {
                LogError(`[AdminUsersAndRoles] Users query failed: ${users.ErrorMessage}`);
            }
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[AdminUsersAndRoles] load failed: ${msg}`);
        } finally {
            this.loading = false;
            this.cdr.detectChanges();
        }
    }

    private toRow(entity: marketingAuthorizedUserEntity): AuthorizedUserRow {
        return toAuthorizedUserRow(entity, this.allUsers);
    }

    async onRoleChange(row: AuthorizedUserRow, newRole: RoleTierLocal): Promise<void> {
        if (newRole === row.role) return;
        if (!isRole(newRole)) {
            this.flashRow(row.id, `Invalid role: ${newRole}`, true);
            return;
        }
        this.saving = true;
        this.cdr.detectChanges();
        try {
            row.entity.Role = newRole;
            const ok = await row.entity.Save();
            if (ok) {
                row.role = newRole;
                this.flashRow(row.id, `Role updated to ${newRole}.`, false);
            } else {
                const errMsg = row.entity.LatestResult?.Message ?? 'unknown error';
                LogError(`[AdminUsersAndRoles] Save failed for AuthorizedUser ${row.id}: ${errMsg}`);
                this.flashRow(row.id, `Save failed: ${errMsg}`, true);
            }
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[AdminUsersAndRoles] onRoleChange threw: ${msg}`);
            this.flashRow(row.id, `Save failed: ${msg}`, true);
        } finally {
            this.saving = false;
            this.cdr.detectChanges();
        }
    }

    async onRevoke(row: AuthorizedUserRow): Promise<void> {
        const label = row.userName || row.userEmail || row.userId;
        const confirmed = typeof window !== 'undefined'
            ? window.confirm(`Revoke marketing access for ${label}?`)
            : true;
        if (!confirmed) return;
        this.saving = true;
        this.cdr.detectChanges();
        try {
            const ok = await row.entity.Delete();
            if (ok) {
                this.rows = this.rows.filter(r => r.id !== row.id);
            } else {
                const errMsg = row.entity.LatestResult?.Message ?? 'unknown error';
                LogError(`[AdminUsersAndRoles] Delete failed for AuthorizedUser ${row.id}: ${errMsg}`);
                this.flashRow(row.id, `Revoke failed: ${errMsg}`, true);
            }
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[AdminUsersAndRoles] onRevoke threw: ${msg}`);
            this.flashRow(row.id, `Revoke failed: ${msg}`, true);
        } finally {
            this.saving = false;
            this.cdr.detectChanges();
        }
    }

    async onGrant(): Promise<void> {
        if (!this.grantUserId || !this.companyId) return;
        if (!isRole(this.grantRole)) {
            LogError(`[AdminUsersAndRoles] onGrant: invalid role ${this.grantRole}`);
            return;
        }
        this.saving = true;
        this.cdr.detectChanges();
        try {
            const md = new Metadata();
            const entity = await md.GetEntityObject<marketingAuthorizedUserEntity>('Authorized Users');
            entity.NewRecord();
            entity.UserID = this.grantUserId;
            entity.CompanyID = this.companyId;
            entity.Role = this.grantRole;
            const ok = await entity.Save();
            if (ok) {
                this.rows = [...this.rows, this.toRow(entity)].sort((a, b) => a.userName.localeCompare(b.userName));
                this.grantUserId = '';
                this.grantRole = 'Operator';
            } else {
                const errMsg = entity.LatestResult?.Message ?? 'unknown error';
                LogError(`[AdminUsersAndRoles] Grant save failed: ${errMsg}`);
            }
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[AdminUsersAndRoles] onGrant threw: ${msg}`);
        } finally {
            this.saving = false;
            this.cdr.detectChanges();
        }
    }

    private flashRow(id: string, message: string, isError: boolean): void {
        this.rowMessage = { ...this.rowMessage, [id]: message };
        this.rowMessageIsError = { ...this.rowMessageIsError, [id]: isError };
        this.cdr.detectChanges();
        if (!isError) {
            setTimeout(() => {
                const next = { ...this.rowMessage };
                delete next[id];
                this.rowMessage = next;
                this.cdr.detectChanges();
            }, 2200);
        }
    }
}

